import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Space, 
  Tag, 
  Empty,
  Badge,
  Progress,
  Button,
  Tooltip
} from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined,
  UserOutlined,
  PushpinOutlined
} from '@ant-design/icons';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { useUser } from '../../contexts/UserContext';
import { sampleSidebarService, FocusTask } from '../../services/mock/sampleSidebarService';
import { PinDialog } from '../PinDialog';
import { ModelType, ModelSelection } from '../../types/models';
import { apiClient } from '../../services/apiClient';

const { Text } = Typography;

interface FocusListProps {
  compact?: boolean;
  showTitle?: boolean;
}

export const FocusList: React.FC<FocusListProps> = ({
  compact = false,
  showTitle = true,
}) => {
  const { dataSource } = useModelSelector();
  const { createThreadFromContext } = useNavigation();
  const { model: chatUIModel, uiRole: chatUIRole } = useChatUI();
  const { user } = useUser();
  
  const [focusTasks, setFocusTasks] = useState<FocusTask[]>([]);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [selectedTaskForPin, setSelectedTaskForPin] = useState<FocusTask | null>(null);

  useEffect(() => {
    void loadFocusTasks();
  }, [dataSource, chatUIModel, user?.id]);

  const loadFocusTasks = async () => {
    if (dataSource === 'sample') {
      try {
        const tasks = await sampleSidebarService.getFocusTasks();
        setFocusTasks(tasks);
      } catch (error) {
        console.error('Failed to load sample focus tasks:', error);
        setFocusTasks([]);
      }
    } else {
      // For live mode, fetch artifact_items filtered by ChatUI model context and user assignment
      try {
        if (!user?.id || !chatUIModel) {
          setFocusTasks([]);
          return;
        }
        
        // Build query params for filtering
        const params = new URLSearchParams();
        params.append('user_id', user.id);
        
        // Add model context filters
        if (chatUIModel.type === ModelType.COMPANY && chatUIModel.artifactTreeId) {
          params.append('artifact_tree_id', chatUIModel.artifactTreeId);
        } else if (chatUIModel.type === ModelType.PRIME && chatUIModel.artifactId) {
          params.append('prime_artifact_id', chatUIModel.artifactId);
        }
        
        // Fetch artifact items assigned to user
        // TODO: Create endpoint GET /artifact-items/focus?user_id=...&artifact_tree_id=...&prime_artifact_id=...
        // For now, use existing endpoint and filter client-side
        const response = await apiClient.get(`/artifact-items?${params.toString()}`);
        const items = response.data?.items || response.data || [];
        
        // Convert artifact items to FocusTask format
        const tasks: FocusTask[] = items
          .filter((item: any) => {
            // Filter by user assignment (check assigned_user_id or owner_id)
            const isAssigned = item.assigned_user_id === user.id || 
                             item.owner_id === user.id ||
                             item.user_id === user.id;
            return isAssigned;
          })
          .map((item: any) => ({
            id: item.id,
            title: item.name || `Item ${item.id}`,
            description: item.description || '',
            type: 'process', // Default type
            priority: item.priority || 'medium',
            status: item.state || 'pending',
            assigneeName: user.fullName || user.username,
            dueDate: item.due_date || item.dueDate,
            artifactId: item.artifact_id,
          }));
        
        setFocusTasks(tasks);
      } catch (error) {
        console.error('Failed to load focus tasks:', error);
        setFocusTasks([]);
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'pending': return 'orange';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'urgent': return <ExclamationCircleOutlined />;
      case 'process': return <ClockCircleOutlined />;
      case 'validation': return <CheckCircleOutlined />;
      case 'improvement': return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const formatDueDate = (dateString?: string) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    
    return date.toLocaleDateString();
  };

  const getProgressPercent = (task: FocusTask) => {
    switch (task.status) {
      case 'completed': return 100;
      case 'in_progress': return 60;
      case 'pending': return 20;
      case 'cancelled': return 0;
      default: return 0;
    }
  };

  const handlePinTask = (task: FocusTask) => {
    setSelectedTaskForPin(task);
    setShowPinDialog(true);
  };

  const handlePinConfirm = async (pinData: { uiRole: string; notes?: string }) => {
    if (!selectedTaskForPin) return;

    const threadName = `${selectedTaskForPin.title} - ${pinData.uiRole}`;
    const context = {
      selectedTaskId: selectedTaskForPin.id,
    };
    
    // Use ChatUI model context, fallback to default
    const model: ModelSelection = chatUIModel || {
      type: ModelType.COMPANY,
    };
    
    await createThreadFromContext(threadName, model, pinData.uiRole, context);
    
    setShowPinDialog(false);
    setSelectedTaskForPin(null);
  };

  const handlePinCancel = () => {
    setShowPinDialog(false);
    setSelectedTaskForPin(null);
  };

  const criticalTasks = focusTasks.filter(t => t.priority === 'critical');
  const highPriorityTasks = focusTasks.filter(t => t.priority === 'high');
  const otherTasks = focusTasks.filter(t => !['critical', 'high'].includes(t.priority));

  if (compact) {
    return (
      <div style={{ 
        marginBottom: 16, 
        width: '100%', 
        maxWidth: '100%', 
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {showTitle && (
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: 'white' }}>
              🎯 Focus ({focusTasks.length})
            </Text>
          </div>
        )}
        
        {focusTasks.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 6,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {focusTasks.slice(0, 3).map(task => (
              <div
                key={task.id}
                style={{
                  padding: '8px 12px',
                  backgroundColor: task.priority === 'critical' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: `1px solid ${task.priority === 'critical' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
                  transition: 'all 0.2s ease',
                }}
                onClick={async () => {
                  const threadName = `${task.title} - ${chatUIRole}`;
                  const context = {
                    selectedTaskId: task.id,
                  };
                  // Use ChatUI model context, fallback to default
                  const model: ModelSelection = chatUIModel || {
                    type: ModelType.COMPANY,
                  };
                  await createThreadFromContext(threadName, model, chatUIRole, context);
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ color: 'white', fontSize: 12 }} ellipsis>
                    {task.title}
                  </Text>
                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                    <Tag color={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Tag>
                    <Tag color={getStatusColor(task.status)}>
                      {task.status}
                    </Tag>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tooltip title="Pin with different UI role">
                    <Button
                      size="small"
                      type="text"
                      icon={<PushpinOutlined />}
                      style={{ color: 'white', fontSize: '10px', padding: '2px 4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePinTask(task);
                      }}
                    />
                  </Tooltip>
                  {getTypeIcon(task.type)}
                </div>
              </div>
            ))}
            
            {focusTasks.length > 3 && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <Text style={{ color: 'white', fontSize: 11 }}>
                  +{focusTasks.length - 3} more
                </Text>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <Text style={{ color: 'white', fontSize: 11 }}>
              No focus tasks
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Text strong style={{ color: 'white' }}>
            🎯 Focus List
          </Text>
          <Badge count={focusTasks.length} showZero={false} />
        </Space>
      }
      size="small"
      style={{ 
        marginBottom: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
      styles={{ body: { padding: '12px' } }}
    >
      {focusTasks.length > 0 ? (
        <div>
          {/* Critical Tasks */}
          {criticalTasks.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: '#ef4444', fontSize: 12 }}>
                🔥 Critical ({criticalTasks.length})
              </Text>
              {criticalTasks.map(task => (
                <div 
                  key={task.id} 
                  style={{ 
                    marginTop: 8, 
                    padding: '8px', 
                    backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                  onClick={async () => {
                    const threadName = `${task.title} - ${chatUIRole}`;
                    const context = {
                      selectedTaskId: task.id,
                    };
                    // Use ChatUI model context, fallback to default
                    const model: ModelSelection = chatUIModel || {
                      type: ModelType.COMPANY,
                    };
                    await createThreadFromContext(threadName, model, chatUIRole, context);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>
                      {task.title}
                    </Text>
                    <Tag color="red">Critical</Tag>
                  </div>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 11, display: 'block', marginTop: 2 }}>
                    {task.description}
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 10 }}>
                      <UserOutlined /> {task.assigneeName} • {formatDueDate(task.dueDate)}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* High Priority Tasks */}
          {highPriorityTasks.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ color: '#f59e0b', fontSize: 12 }}>
                ⚡ High Priority ({highPriorityTasks.length})
              </Text>
              {highPriorityTasks.map(task => (
                <div 
                  key={task.id} 
                  style={{ 
                    marginTop: 8, 
                    padding: '8px', 
                    backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                  onClick={async () => {
                    const threadName = `${task.title} - ${chatUIRole}`;
                    const context = {
                      selectedTaskId: task.id,
                    };
                    // Use ChatUI model context, fallback to default
                    const model: ModelSelection = chatUIModel || {
                      type: ModelType.COMPANY,
                    };
                    await createThreadFromContext(threadName, model, chatUIRole, context);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>
                      {task.title}
                    </Text>
                    <Tag color="orange">High</Tag>
                  </div>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 11, display: 'block', marginTop: 2 }}>
                    {task.description}
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    <Progress 
                      percent={getProgressPercent(task)} 
                      size="small" 
                      strokeColor={task.status === 'completed' ? '#10b981' : '#3b82f6'}
                      showInfo={false}
                    />
                    <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 10 }}>
                      <UserOutlined /> {task.assigneeName} • {formatDueDate(task.dueDate)}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Other Tasks */}
          {otherTasks.length > 0 && (
            <div>
              <Text strong style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 12 }}>
                📋 Other Tasks ({otherTasks.length})
              </Text>
              {otherTasks.slice(0, 2).map(task => (
                <div 
                  key={task.id} 
                  style={{ 
                    marginTop: 8, 
                    padding: '8px', 
                    backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                  onClick={async () => {
                    const threadName = `${task.title} - ${chatUIRole}`;
                    const context = {
                      selectedTaskId: task.id,
                    };
                    // Use ChatUI model context, fallback to default
                    const model: ModelSelection = chatUIModel || {
                      type: ModelType.COMPANY,
                    };
                    await createThreadFromContext(threadName, model, chatUIRole, context);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text style={{ color: 'white', fontSize: 12 }}>
                      {task.title}
                    </Text>
                    <Tag color={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Tag>
                  </div>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 10, marginTop: 2 }}>
                    <UserOutlined /> {task.assigneeName}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              No focus tasks
            </Text>
          }
        />
      )}

      {/* Pin Dialog */}
      <PinDialog
        visible={showPinDialog}
        onCancel={handlePinCancel}
        onOk={handlePinConfirm}
        itemName={selectedTaskForPin?.title || ''}
        itemType="task"
        initialUIRole={chatUIRole}
      />
    </Card>
  );
};
